/* ConfigLineRenderer.js */

"use strict";
const PopUpWidget = require("@gluenetworks/steve_ui_widgets").PopUp;
const _ = require("lodash");
const LOG_MAX_LENGTH = 5000;

class ConfigLineRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("links-column");
        this._rootElem.component = this._model;
        this._popUpWidget = undefined;
        this._configLinesLoaded = false; // Track if config lines have been loaded
        this._configLinesLoading = false; // Track if currently loading
        this._loadingPromise = null; // Store the loading promise for concurrent requests
        this._configLines = null; // Cache for loaded config lines

        this._executionPlanLogIcon = document.createElement("actionable-icon-gl");
        this._executionPlanLogIcon.dataset.icon = "common.log_link";
        this._executionPlanLogIcon.setAttribute("title", "Config Lines (click to open)");

        this._logFile = document.createElement("log-display-gl");

        this._registerListeners();
        this.render();
    }

    /* ******************************************
    /* START PRIVATE COMPONENT RENDERER METHODS
    /* ****************************************** */

    _shutdown() {
        return new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();

            // Clean up popup widget and its event listeners
            if (this._popUpWidget) {
                this._popUpWidget.removeAllListeners("PopUpVisible");
                this._popUpWidget = undefined;
            }

            // Clean up DOM elements
            if (this._executionPlanLogIcon) {
                this._executionPlanLogIcon.remove();
                this._executionPlanLogIcon = undefined;
            }

            // Clean up cached data and state
            this._configLines = undefined;
            this._configLinesLoaded = undefined;
            this._configLinesLoading = undefined;
            this._loadingPromise = undefined;

            // Clean up references
            this._model = undefined;
            if (this._rootElem) {
                this._rootElem.remove();
                this._rootElem = undefined;
            }
            this._logFile = undefined;

            resolve();
        });
    }

    _registerListeners() {
        this._model.on("Links", (this._onLinks = this._onLinks.bind(this)));
        this._executionPlanLogIcon.addEventListener(
            "click",
            (this._onIconClick = this._onIconClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeAllListeners("Links");
        if (this._executionPlanLogIcon) {
            this._executionPlanLogIcon.removeEventListener("click", this._onIconClick);
        }
    }

    _onIconClick(event) {
        // Keep the click off the row, which would select it or open the detail editor.
        event.stopPropagation();
        // Config lines are fetched lazily, so make sure they have arrived before opening.
        this._loadConfigLinesOnDemand()
            .then(() => {
                if (!this._model) {
                    return undefined;
                }
                return this._model.openFullText(this._getFullText());
            })
            .catch(() => {
                // The load failure is already handled and surfaced in the popup.
            });
    }

    _getFullText() {
        let logs = this._configLines || [];
        if (logs.length > LOG_MAX_LENGTH) {
            logs = logs.slice(0, LOG_MAX_LENGTH);
            logs.push("...");
            logs.push("Config Lines have been truncated");
            logs.push(`Exceeding maximum length of ${LOG_MAX_LENGTH} lines`);
        }
        return logs.length ? logs.join("\n") : "No config line available";
    }

    _setConfigLineData() {
        if (this._rootElem === null) {
            return;
        }

        // Initially show loading message
        this._showLoadingState();

        let viewExecutionPlanElement = document.createElement("view-more-pop-up");
        viewExecutionPlanElement.classList.add("config-line-popup");
        viewExecutionPlanElement.innerHTML = this._getToolTipContent();
        this._popUpWidget = new PopUpWidget({
            content: viewExecutionPlanElement.outerHTML,
            anchor: this._executionPlanLogIcon,
            anchorPoint: "left",
            displayDirection: "bottomLeft",
            openOnHover: true,
            parent: viewExecutionPlanElement.parentNode,
        });

        // Listen to PopUpVisible event to lazy load config lines on hover
        this._popUpWidget.on("PopUpVisible", () => this._loadConfigLinesOnDemand());
    }

    _showLoadingState() {
        this._logFile.innerHTML = "";
        const loadingMsg = document.createElement("p");
        loadingMsg.classList.add("loading-message");
        const messageSpan = document.createElement("span");
        messageSpan.classList.add("message");
        loadingMsg.appendChild(messageSpan);
        this._logFile.appendChild(loadingMsg);
    }

    _loadConfigLinesOnDemand() {
        // Scenario 1: Already loaded - use cached data
        if (this._configLinesLoaded && this._configLines) {
            return Promise.resolve();
        }

        // Scenario 2: Currently loading - return the existing promise
        if (this._configLinesLoading && this._loadingPromise) {
            return this._loadingPromise;
        }

        // Scenario 3: First time load - fetch from backend
        this._configLinesLoading = true;

        // Show loading state in popup
        this._showLoadingState();
        this._updatePopupContent();

        // Get the entity from the model - this should always exist since it's from the grid row
        const entity = this._model.getValue();
        if (!entity) {
            this._configLinesLoading = false;
            this._configLinesLoaded = true;
            this._updateConfigLineDisplay([]);
            return Promise.resolve();
        }

        const entityId = entity.getId ? entity.getId() : entity.id || entity._id;

        // Get resultEntityDef and tenantId from model component
        const resultEntityDef = this._model._resultEntityDef;
        const tenantId = this._model._activeOrgId;

        if (!resultEntityDef || !tenantId) {
            this._configLinesLoading = false;
            this._configLinesLoaded = true;
            this._updateConfigLineDisplay([]);
            this._updatePopupContent();
            return Promise.resolve();
        }

        const fields = {configLines: true};

        // Store the promise so concurrent requests can wait for it
        this._loadingPromise = resultEntityDef
            .loadConfigAuditResult(tenantId, entityId, fields)
            .then((results) => {
                // If the user moves away from the screen after leaving the loading state
                if (!this._model) {
                    return Promise.resolve();
                }
                if (!_.isEmpty(results)) {
                    const configLines = results.getConfigLines
                        ? results.getConfigLines()
                        : results.configLines || [];
                    this._configLines = configLines || [];
                    this._configLinesLoaded = true;
                    this._configLinesLoading = false;
                    this._loadingPromise = null;
                    this._updateConfigLineDisplay(this._configLines);
                    this._updatePopupContent();
                } else {
                    this._configLines = [];
                    this._configLinesLoaded = true;
                    this._configLinesLoading = false;
                    this._loadingPromise = null;
                    this._updateConfigLineDisplay([]);
                    this._updatePopupContent();
                }
                return Promise.resolve();
            })
            .catch((error) => {
                this._configLines = [];
                this._configLinesLoading = false;
                this._configLinesLoaded = true;
                this._loadingPromise = null;
                this._updateConfigLineDisplay([]);
                this._updatePopupContent();
            });

        return this._loadingPromise;
    }

    _updateConfigLineDisplay(configLines) {
        // Clear existing log file content
        this._logFile.innerHTML = "";

        // Re-render with new config lines
        this._setConfigLine(configLines);
    }

    _updatePopupContent() {
        // Update popup content if it exists
        if (this._popUpWidget && this._popUpWidget.setContent) {
            let viewExecutionPlanElement = document.createElement("view-more-pop-up");
            viewExecutionPlanElement.classList.add("config-line-popup");
            viewExecutionPlanElement.innerHTML = this._getToolTipContent();
            this._popUpWidget.setContent(viewExecutionPlanElement.outerHTML);
        }
    }

    _getToolTipContent() {
        return `<span class="arrow"><i></i></span><div class="main-container p-b-0">${this._logFile.outerHTML}</div>`;
    }

    /* *****************************************
    /* END PRIVATE COMPONENT RENDERER METHODS
    /* START PUBLIC COMPONENT RENDERER METHODS
    /* ***************************************** */
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._setLinks(this._model.getLinks());
        this._setConfigLineData();
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /* *****************************************
    /* END COMPONENT RENDERER METHODS
    /* START HANDLER METHODS
    /* ***************************************** */

    _onLinks(newVal) {
        this._setLinks(newVal);
    }

    _setConfigLine(logs) {
        if (!_.isEmpty(logs) && logs.length > LOG_MAX_LENGTH) {
            logs = logs.slice(0, LOG_MAX_LENGTH);
            logs.push("...");
            logs.push("Config Lines have been truncated");
            logs.push(`Exceeding maximum length of ${LOG_MAX_LENGTH} lines`);
        }

        let wrapper = document.createElement("div");
        _.forEach(logs, (log) => {
            wrapper.appendChild(this._createLogItem(log));
        });
        if (_.isEmpty(logs)) {
            let emptyMsg = document.createElement("span");
            emptyMsg.innerHTML = "No config line available";
            wrapper.appendChild(emptyMsg);
        }
        this._logFile.appendChild(wrapper);
    }

    _createLogItem(log) {
        let logEntry = document.createElement("p");
        let message = document.createElement("span");
        message.classList.add("message");
        message.innerText = log;
        logEntry.appendChild(message);
        return logEntry;
    }

    /* *****************************************
    /* END HANDLER METHODS
    /* START PRIVATE API METHODS
    /* ***************************************** */

    _setLinks() {
        // Always show the icon - config lines will be loaded on hover
        // The entity exists, so show the icon regardless of whether data is loaded
        this._rootElem.innerHTML = "";
        this._rootElem.appendChild(this._executionPlanLogIcon);
    }

    /* *****************************************
    /* END PRIVATE API METHODS
    /* ***************************************** */
}

module.exports = ConfigLineRenderer;
